import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTwitter,
  faFacebook,
  faLinkedin,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import {
  faDownload,
  faFolderOpen,
  faImage,
  faArchive,
} from "@fortawesome/free-solid-svg-icons";
import JSZip from "jszip";
import FileSaver from "file-saver";
import "./SocialMediaFormats.css";

const SocialMediaFormats = ({ socialMediaPaths }) => {
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingItem, setDownloadingItem] = useState(null);

  const platformConfig = {
    twitter: {
      icon: faTwitter,
      color: "bg-blue-500",
      name: "Twitter",
      description: "Perfect for tweets and profile headers",
    },
    facebook: {
      icon: faFacebook,
      color: "bg-blue-700",
      name: "Facebook",
      description: "Optimized for posts and cover photos",
    },
    linkedin: {
      icon: faLinkedin,
      color: "bg-blue-600",
      name: "LinkedIn",
      description: "Professional networking formats",
    },
    whatsapp: {
      icon: faWhatsapp,
      color: "bg-green-600",
      name: "WhatsApp",
      description: "Mobile-friendly sharing formats",
    },
  };
  const downloadImage = async (url, platform, index = null) => {
    const downloadId = `${platform}-${index || "single"}`;
    setDownloadingItem(downloadId);

    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = url.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setTimeout(() => setDownloadingItem(null), 1000);
    }
  };

  const downloadAll = async (paths, folderName) => {
    setDownloadingAll(true);
    const zip = new JSZip();
    const folder = zip.folder(folderName);

    try {
      for (const [index, path] of paths.entries()) {
        try {
          const response = await fetch(path);
          const blob = await response.blob();
          folder.file(
            `${folderName}_${index + 1}_${path.split("/").pop()}`,
            blob
          );
        } catch (error) {
          console.error(`Failed to fetch image ${index}:`, error);
        }
      }

      const content = await zip.generateAsync({ type: "blob" });
      FileSaver.saveAs(content, `${folderName}.zip`);
    } catch (error) {
      console.error("Failed to create zip:", error);
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Download All Button */}
      <div className="text-center">
        <button
          onClick={() => {
            const allPaths = Object.values(socialMediaPaths)
              .flat()
              .filter((path) => path !== null);
            downloadAll(allPaths, "all_social_media_formats");
          }}
          disabled={downloadingAll}
          className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold rounded-xl hover:from-purple-700 hover:to-purple-800 hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {downloadingAll ? (
            <>
              <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
              <span>Creating ZIP...</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faArchive} className="text-lg" />
              <span>Download All Formats</span>
            </>
          )}
        </button>
      </div>

      {/* Platform Grids */}
      <div className="grid md:grid-cols-2 gap-8">
        {Object.entries(socialMediaPaths)
          .filter(([_, paths]) => paths !== null)
          .map(([category, paths]) => {
            const config = platformConfig[category] || {
              icon: faImage,
              color: "bg-gray-600",
              name: category.charAt(0).toUpperCase() + category.slice(1),
              description: "Social media format",
            };

            return (
              <div
                key={category}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
              >
                {/* Platform Header */}
                <div className={`${config.color} p-6 text-white`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <FontAwesomeIcon icon={config.icon} className="text-xl" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{config.name}</h3>
                      <p className="text-white/80">{config.description}</p>
                    </div>
                  </div>
                </div>

                {/* Images Grid */}
                <div className="p-6">
                  {Array.isArray(paths) ? (
                    /* Multiple Images */
                    <div className="space-y-6">
                      <div className="grid gap-4">
                        {paths.map((path, index) => {
                          const downloadId = `${category}-${index}`;
                          const isDownloading = downloadingItem === downloadId;

                          return (
                            <div key={index} className="group relative">
                              <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden">
                                <img
                                  src={path}
                                  alt={`${config.name} format ${index + 1}`}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />

                                {/* Overlay on Hover */}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <button
                                    onClick={() =>
                                      downloadImage(path, category, index)
                                    }
                                    disabled={isDownloading}
                                    className="px-6 py-3 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
                                  >
                                    {isDownloading ? (
                                      <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-900 rounded-full animate-spin"></div>
                                        <span>Downloading...</span>
                                      </div>
                                    ) : (
                                      <>
                                        <FontAwesomeIcon
                                          icon={faDownload}
                                          className="mr-2"
                                        />
                                        Download
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>

                              <div className="mt-3 text-center">
                                <p className="text-sm font-medium text-gray-700">
                                  Format {index + 1}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Download All for Category */}
                      <div className="text-center pt-4 border-t border-gray-200">
                        <button
                          onClick={() =>
                            downloadAll(paths, category.replace(/-/g, "_"))
                          }
                          disabled={downloadingAll}
                          className={`inline-flex items-center gap-2 px-6 py-3 ${config.color} text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <FontAwesomeIcon icon={faFolderOpen} />
                          <span>Download All {config.name}</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Single Image */
                    <div className="group relative">
                      <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden">
                        <img
                          src={paths}
                          alt={`${config.name} format`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />

                        {/* Overlay on Hover */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            onClick={() => downloadImage(paths, category)}
                            disabled={downloadingItem === `${category}-single`}
                            className="px-6 py-3 bg-white text-gray-900 font-semibold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
                          >
                            {downloadingItem === `${category}-single` ? (
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-900 rounded-full animate-spin"></div>
                                <span>Downloading...</span>
                              </div>
                            ) : (
                              <>
                                <FontAwesomeIcon
                                  icon={faDownload}
                                  className="mr-2"
                                />
                                Download {config.name}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default SocialMediaFormats;
